import Vue from 'vue';
import Router, {Route} from 'vue-router';
import store from './store/store';
import VueAnalytics from 'vue-analytics';
import { ApiService } from './services/api.base.service';
import { GlobalTokenManager } from '@/classes/axios.cancel.token';
import {Organisation} from '@/types';

export enum ApplicationArea {
  Public,
  Misc,
  Home,
  Publish,
  Compose,
  Consume,
  Fabric,
  Admin,
  Subscriptions,
  Inventory,
  Accounts,
}

// Flag to indcate whether switch org should force a re-render
let forceRender: boolean = true;

class RouterHelperFuncs {

  public isHome() {
    return router.currentRoute.meta.area === ApplicationArea.Home;
  }

  public isCompose() {
    return router.currentRoute.meta.area === ApplicationArea.Compose;
  }

  public isConsume() {
    return router.currentRoute.meta.area === ApplicationArea.Consume;
  }

  public isPublish() {
    return router.currentRoute.meta.area === ApplicationArea.Publish;
  }

  public isAccounts() {
    return router.currentRoute.meta.area === ApplicationArea.Accounts;
  }

  public isSubscriptions() {
    return router.currentRoute.meta.area === ApplicationArea.Subscriptions;
  }

  public isInventory() {
    return router.currentRoute.meta.area === ApplicationArea.Inventory;
  }

  public isFabric() {
    return router.currentRoute.meta.area === ApplicationArea.Fabric;
  }

  public isAdmin() {
    return router.currentRoute.meta.area === ApplicationArea.Admin;
  }

  public isProjectsList() {
    return router.currentRoute.path!.substr(0, 9) === '/projects';
  }

  public async switchOrganisation(organisationId: number) {
    const currentRoute = router.currentRoute;
    if (currentRoute.meta && currentRoute.meta.organisationSwitchRedirect) {
      forceRender = false;
      router.push(`/organisation/${organisationId}/${currentRoute.meta.organisationSwitchRedirect}`);
      return;
    }
    if (currentRoute.meta.area && !currentRoute.meta.organisationSwitchNoNav && (
      currentRoute.meta.area === ApplicationArea.Compose ||
      currentRoute.meta.area === ApplicationArea.Inventory ||
      currentRoute.meta.area === ApplicationArea.Publish ||
      currentRoute.meta.area === ApplicationArea.Consume)) {
      if (currentRoute.path.includes('/organisation/')) {
        const idx: number = currentRoute.path.indexOf('/', 14);
        router.push(`/organisation/${organisationId}${currentRoute.path.substr(idx)}`);
        return;
      } else {
        router.push(`/organisation/${organisationId}${currentRoute.path}`);
        return;
      }
    } else {
      store.commit('setLastOrganisation', organisationId);
      await store.dispatch('changeOrganisationAsync', {orgId: organisationId, forceRender: false});
    }
  }

  public async switchAccount(accountId: number) {
    const currentRoute = router.currentRoute;
    if (currentRoute.meta.area && currentRoute.meta.area === ApplicationArea.Accounts) {
      if (currentRoute.path.includes('/account/')) {
        const idx: number = currentRoute.path.indexOf('/', 9);
        router.push(`/account/${accountId}${currentRoute.path.substr(idx)}`);
        return;
      } else {
        router.push(`/account/${accountId}${currentRoute.path}`);
        return;
      }
    } else {
      await store.dispatch('changeAccountAsync', {accountId, forceRender: false});
    }
  }

  public async setLogin() {
    const loginData: any = store.getters.login;
    if (loginData && loginData.token && ApiService.token !== loginData.token) {
      store.commit('addToken', { token: loginData.token });
      await store.dispatch('userDetailsAsync');
    }
  }

  public async setOrganisation(to: Route, from: Route, next: any) {
    if (store.getters.organisations.organisations.length === 0) {
      await store.dispatch('addOrganisationsAsync');
    }
    if (to.params && to.params.organisationId !== undefined) {
      const organisationId: number = Number(to.params.organisationId);
      // Redirect if we get passed org id 0 (Means redirect happened before org set)
      if (!organisationId) {
        next(to.path.replace('organisation/0', `organisation/${store.getters.organisations.activeOrganisationId}`));
        return false;
      }

      // Only switch org if changed
      if (store.getters.organisations.activeOrganisationId !== organisationId) {
        if (store.getters.organisations.organisations.some((org: any) => org.id === organisationId)) {
          // Prevent force re-render if route changed
          if (from.name && to.name !== from.name) {
            forceRender = false;
          }

          store.commit('setLastOrganisation', organisationId);
          await store.dispatch('changeOrganisationAsync', {orgId: organisationId, forceRender});

          // Reset force render back to true (may have been set false)
          forceRender = true;
        } else {
          // We don't know about this organisation
          next('/404');
          return false;
        }
      }
    }
    return true;
  }

  public async setAccount(to: Route, from: Route, next: any) {
    if (store.getters.accounts.accounts.length === 0) {
      await store.dispatch('addAccountsAsync');
    }
    // if (!store.getters.accounts.activeAccountId && store.getters.accounts.accounts[0]) {
    //   store.commit('selectAccount', store.getters.accounts.accounts[0].id);
    // }
    if (to.params && to.params.accountId !== undefined) {
      const accountId: number = Number(to.params.accountId);
      // Redirect if we get passed account id 0 (Means redirect happened before org set)
      if (!accountId) {
        await store.dispatch('changeAccountAsync', {accountId: store.getters.accounts.accounts[0].id, forceRender: true});
        next(to.path.replace('account/0', `account/${store.getters.accounts.activeAccountId}`));
        return false;
      }

      // Only switch account if changed
      if (store.getters.activeAccountId !== accountId) {
        if (store.getters.accounts.accounts.some((acc: any) => acc.id === accountId)) {
          // Prevent force re-render if route changed
          if (from.name && to.name !== from.name) {
            forceRender = false;
          }
          await store.dispatch('changeAccountAsync', {accountId, forceRender});

          // Reset force render back to true (may have been set false)
          forceRender = true;
        } else {
          // We don't know about this account
          next('/404');
          return false;
        }
      }
    }
    return true;
  }

  public redirectHandler(to: Route): string {
    let path: string = '';
    if (!to.matched.length) {
      path = to.path;
    } else {
      path = to.matched[0].path;
    }
    path = path.replace('/organisation', '');
    return `/organisation/${store.getters.organisations.activeOrganisationId}${path}`;
  }

  public home(): void {
    const validLandingPage: string | null = this.getValidLandingPage();

    if (validLandingPage && router.currentRoute.path !== validLandingPage) {
      router.push(validLandingPage);
    } else {
      router.go(0);
    }
  }

  public getValidLandingPage(): string {
    const landingPages: { [k: string]: string} = {
      'administration:view': `/organisation/${store.getters.organisations.activeOrganisationId}/projectcomposer`,
      'compose:view': '/dashboard',
      'publish:view': `/organisation/${store.getters.organisations.activeOrganisationId}/publish`,
      'consume:view': `/organisation/${store.getters.organisations.activeOrganisationId}/consume`,
      'inventory:view': `/organisation/${store.getters.organisations.activeOrganisationId}/inventory`,
    };

    const permissions = Object.keys(landingPages);

    for (const perm of permissions) {
      if (store.getters.hasPermission(perm)) {
        return landingPages[perm];
      }
    }

    return '/404';
  }
}

export const RouterHelper = new RouterHelperFuncs();

Vue.use(Router);

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import(/* webpackChunkName: "login" */ './views/Login.vue'),
      meta: {
        area: ApplicationArea.Public,
      },
    },
    {
      path: '/',
      redirect: RouterHelper.getValidLandingPage,
    },
    {
      path: '/organisation/settings',
      name: 'organisationsettings-base',
      redirect: RouterHelper.redirectHandler,
      meta: {
        friendlyName: 'Organisation Settings',
        area: ApplicationArea.Compose,
      },
    },
    {
      path: '/organisation/:organisationId/settings',
      name: '',
      component: () => import(/* webpackChunkName: "organisationsettings" */ './views/organisations/OrganisationSettings.vue'),
      meta: {
        friendlyName: 'Organisation Settings',
        area: ApplicationArea.Compose,
      },
      children: [
        {
          path: '',
          name: 'organisationdetails',
          component: () => import(/* webpackChunkName: "organisationdetails" */ './components/settings/Details.vue'),
          meta: {
            friendlyName: 'Organisation Details',
            area: ApplicationArea.Compose,
          },
        },
        {
          path: 'users',
          name: 'users',
          component: () => import(/* webpackChunkName: "users" */ './components/settings/Users.vue'),
          meta: {
            friendlyName: 'Users',
            area: ApplicationArea.Compose,
          },
        },
        {
          path: 'apiKeys',
          name: 'apiKeys',
          component: () => import(/* webpackChunkName: "apiKeys" */ './components/settings/APIKeys.vue'),
          meta: {
            friendlyName: 'API Keys',
            area: ApplicationArea.Compose,
          },
        },
      ],
    },
    {
      path: '/devices',
      name: 'devices-base',
      redirect: RouterHelper.redirectHandler,
      meta: {
        area: ApplicationArea.Compose,
        friendlyName: 'devices',
      },
    },
    {
      path: '/organisation/:organisationId/devices',
      name: 'devices',
      component: () => import(/* webpackChunkName: "devices" */ './views/Devices.vue'),
      meta: {
        area: ApplicationArea.Compose,
        friendlyName: 'devices',
      },
      children: [
        {
          path: ':id',
          name: 'details',
          props: true,
          component: () => import(/* webpackChunkName: "details" */ './views/DeviceDetails.vue'),
          meta: {
            area: ApplicationArea.Compose,
            friendlyName: 'device details',
            organisationSwitchRedirect: 'details',
          },
        },
      ],
    },
    {
      path: '/gateways',
      name: 'gateways-base',
      redirect: RouterHelper.redirectHandler,
      meta: {
        area: ApplicationArea.Compose,
        friendlyName: 'gateways',
      },
      children: [
        {
          path: ':id',
          name: 'gatewaydetails-base',
          props: true,
          component: () => import(/* webpackChunkName: "gatewaydetails-base" */ './views/GatewayDetails.vue'),
          meta: {
            area: ApplicationArea.Compose,
            friendlyName: 'gateway details',
            organisationSwitchRedirect: 'gatewaydetails-base',
          },
        },
      ],
    },
    {
      path: '/organisation/:organisationId/gateways',
      name: 'gateways',
      component: () => import(/* webpackChunkName: "gateways" */ './views/Gateways.vue'),
      meta: {
        area: ApplicationArea.Compose,
        friendlyName: 'gateways',
      },
      children: [
        {
          path: ':id',
          name: 'gatewaydetails',
          props: true,
          component: () => import(/* webpackChunkName: "gatewaydetails" */ './views/GatewayDetails.vue'),
          meta: {
            area: ApplicationArea.Compose,
            friendlyName: 'gateway details',
            organisationSwitchRedirect: 'gatewaydetails',
          },
        },
      ],
    },
    {
      path: '/tasks',
      name: 'tasks-base',
      redirect: RouterHelper.redirectHandler,
      meta: {
        area: ApplicationArea.Compose,
        friendlyName: 'tasks',
      },
    },
    {
      path: '/organisation/:organisationId/tasks',
      name: 'tasks',
      component: () => import(/* webpackChunkName: "devices" */ './views/Tasks.vue'),
      meta: {
        area: ApplicationArea.Compose,
        friendlyName: 'tasks',
      },
      children: [
        {
          path: ':id',
          name: 'taskdetails',
          props: true,
          component: () => import(/* webpackChunkName: "details" */ './views/TaskInformation.vue'),
          meta: {
            area: ApplicationArea.Compose,
            friendlyName: 'task details',
            organisationSwitchRedirect: 'tasks',
          },
        },
      ],
    },
    {
      path: '/catalogue',
      name: 'catalogue-base',
      redirect: RouterHelper.redirectHandler,
      meta: {
        area: ApplicationArea.Compose,
        friendlyName: 'catalogue',
      },
    },
    {
      path: '/organisation/:organisationId/catalogue',
      name: 'catalogue',
      component: () => import(/* webpackChunkName: "catalogue" */ './views/Catalogue.vue'),
      meta: {
        area: ApplicationArea.Compose,
        friendlyName: 'catalogue',
      },
    },
    {
      path: '/device/:id',
      name: 'device-base',
      redirect: RouterHelper.redirectHandler,
      meta: {
        area: ApplicationArea.Compose,
        friendlyName: 'device',
        organisationSwitchRedirect: 'devices',
      },
    },
    {
      path: '/organisation/:organisationId/projectcomposer',
      name: 'projectcomposer',
      component: () => import(/* webpackChunkName: "locations" */ './views/ProjectComposer.vue'),
      meta: {
        area: ApplicationArea.Compose,
        friendlyName: 'projectcomposer',
      },
      children: [
        {
          path: 'devices/:id',
          name: 'composer-devicedetails',
          props: true,
          component: () => import(/* webpackChunkName: "details" */ './views/DeviceDetails.vue'),
          meta: {
            area: ApplicationArea.Compose,
            friendlyName: 'device details',
            organisationSwitchRedirect: 'details',
          },
        },
        {
          path: 'gateways/:id',
          name: 'composer-gatewaydetails',
          props: true,
          component: () => import(/* webpackChunkName: "gatewaydetails" */ './views/GatewayDetails.vue'),
          meta: {
            area: ApplicationArea.Compose,
            friendlyName: 'gateway details',
            organisationSwitchRedirect: 'gatewaydetails',
          },
        },
      ],
    },
    {
      path: '/organisation/:organisationId/inventory',
      name: 'inventory-devices',
      component: () => import(/* webpackChunkName: "catalogue" */ './views/InventoryDevices.vue'),
      meta: {
        area: ApplicationArea.Inventory,
        friendlyName: 'inventory-devices',
      },
      children: [
        {
          path: 'devices/:id',
          name: 'inventory-details',
          props: true,
          component: () => import(/* webpackChunkName: "details" */ './views/DeviceDetails.vue'),
          meta: {
            area: ApplicationArea.Inventory,
            friendlyName: 'device details',
          },
        },
      ],
    },
    {
      path: '/organisation/:organisationId/inventory/gateways',
      name: 'inventory-gateways',
      component: () => import(/* webpackChunkName: "details" */ './views/InventoryGateways.vue'),
      meta: {
        area: ApplicationArea.Inventory,
        friendlyName: 'inventory-gateways',
      },
      children: [
        {
          path: ':id',
          name: 'inventory-gateway-details',
          props: true,
          component: () => import(/* webpackChunkName: "details" */ './views/GatewayDetails.vue'),
          meta: {
            area: ApplicationArea.Inventory,
            friendlyName: 'gateway details',
          },
        },
      ],
    },
    {
      path: '/organisation/:organisationId/inventory/catalogue',
      name: 'inventory-catalogue',
      component: () => import(/* webpackChunkName: "catalogue" */ './views/Catalogue.vue'),
      meta: {
        area: ApplicationArea.Inventory,
        friendlyName: 'inventory-catalogue',
      },
    },
    {
      path: '/organisation/:organisationId/consume',
      name: 'consume-home',
      component: () => import(/* webpackChunkName: "consume" */ './views/consume/Applications.vue'),
      meta: {
        area: ApplicationArea.Consume,
        friendlyName: 'consume',
      },
    },
    {
      path: '/consume',
      name: 'consume-base',
      redirect: RouterHelper.redirectHandler,
      meta: {
        area: ApplicationArea.Consume,
        friendlyName: 'consume',
      },
    },
    {
      path: '/organisation/:organisationId/consume/explorer',
      name: 'explorer',
      component: () => import(/* webpackChunkName: "explorer" */ './views/consume/DataExplorer.vue'),
      meta: {
        friendlyName: 'Data Explorer',
        area: ApplicationArea.Consume,
      },
    },
    {
      path: '/organisation/:organisationId/consume/:id',
      name: 'consume-app',
      component: () => import(/* webpackChunkName: "consume-app" */ './views/consume/ApplicationContainer.vue'),
      meta: {
        area: ApplicationArea.Consume,
        friendlyName: 'Consume Application',
      },
    },
    {
      path: '/consume/:id',
      name: 'consume-app-base',
      redirect: RouterHelper.redirectHandler,
      meta: {
        area: ApplicationArea.Consume,
        friendlyName: 'Consume Application',
      },
    },
    {
      path: '/publish',
      name: 'publish-base',
      redirect: RouterHelper.redirectHandler,
      meta: {
        area: ApplicationArea.Publish,
        friendlyName: 'publish',
      },
    },
    {
      path: '/organisation/:organisationId/publish',
      name: 'publish',
      component: () => import(/* webpackChunkName: "publish" */ './views/publish/Publish.vue'),
      meta: {
        area: ApplicationArea.Publish,
        friendlyName: 'publish',
      },
    },
    {
      path: '/organisation/:organisationId/publish/flow-editor',
      name: 'flow-editor',
      component: () => import(/* webpackChunkName: "flowEditor" */ './views/publish/FlowEditor.vue'),
      meta: {
        friendlyName: 'Flow Editor',
        area: ApplicationArea.Publish,
      },
    },
    {
      path: '/organisation/:organisationId/publish/launch-flow-editor',
      name: 'launch-flow-editor',
      component: () => import(/* webpackChunkName: "flowEditor" */ './views/publish/LaunchFlowEditor.vue'),
      meta: {
        friendlyName: 'Launch Flow Editor',
        area: ApplicationArea.Admin,
      },
    },
    {
      path: '/organisation/:organisationId/publish/flow-ui',
      name: 'flow-ui',
      component: () => import(/* webpackChunkName: "flowUI" */ './views/publish/FlowUI.vue'),
      meta: {
        friendlyName: 'Flow UI',
        area: ApplicationArea.Publish,
      },
    },
    {
      path: '/organisation/:organisationId/publish/datasets',
      name: 'datasets',
      component: () => import(/* webpackChunkName: "dataSets" */ './views/publish/DataSets.vue'),
      meta: {
        friendlyName: 'Data Sets',
        area: ApplicationArea.Publish,
      },
    },
    {
      path: '/organisation/:organisationId/publish/dataset/manage/:id',
      name: 'dataset',
      component: () => import(/* webpackChunkName: "dataSetManage" */ './views/publish/ManageDataSetDetails.vue'),
      props: true,
      meta: {
        friendlyName: 'Manage Data Set',
        area: ApplicationArea.Publish,
      },
    },
    {
      path: '/organisation/:organisationId/publish/integrations',
      name: 'integrations',
      component: () => import(/* webpackChunkName: "integrations" */ './views/publish/Integrations.vue'),
      meta: {
        friendlyName: 'Integrations',
        area: ApplicationArea.Publish,
      },
    },
    {
      path: '/organisation/:organisationId/publish/management',
      name: 'management-integrations',
      component: () => import(/* webpackChunkName: "management-integrations" */ './views/publish/Management.vue'),
      meta: {
        friendlyName: 'Management Integrations',
        area: ApplicationArea.Publish,
      },
    },
    {
      path: '/organisation/:organisationId/publish/rules',
      name: 'event-rules',
      component: () => import(/* webpackChunkName: "event-rules" */ './views/publish/EventRules.vue'),
      meta: {
        friendlyName: 'Rules',
        area: ApplicationArea.Publish,
      },
    },
    {
      path: '/organisation/:organisationId/publish/events',
      name: 'device-events',
      component: () => import(/* webpackChunkName: "deviceEvents" */ './views/publish/DeviceEvents.vue'),
      meta: {
        friendlyName: 'Device Events',
        area: ApplicationArea.Publish,
      },
    },
    {
      path: '/organisations',
      name: 'organisations',
      component: () => import(/* webpackChunkName: "organisation" */ './views/OrganisationList.vue'),
      meta: {
        area: ApplicationArea.Compose,
        organisationSwitchNoNav: true,
      },
    },
    {
      path: '/admin/settings',
      name: 'adminsettings',
      component: () => import(/* webpackChunkName: "adminsettings" */ './views/admin/AdminSettings.vue'),
      meta: {
        area: ApplicationArea.Admin,
        friendlyName: 'Portal Settings',
      },
    },
    {
      path: '/admin/settings/applications',
      name: 'adminapplications',
      component: () => import(/* webpackChunkName: "adminapplications" */ './views/admin/AdminApplications.vue'),
      meta: {
        area: ApplicationArea.Admin,
        friendlyName: 'Portal Applications',
      },
    },
    {
      path: '/admin/settings/apps',
      name: 'adminapps',
      component: () => import(/* webpackChunkName: "adminapps" */ './views/admin/AdminApps.vue'),
      meta: {
        area: ApplicationArea.Admin,
        friendlyName: 'Daizy Apps',
      },
    },
    {
      path: '/admin/settings/banners',
      name: 'adminbanners',
      component: () => import(/* webpackChunkName: "adminapps" */ './views/admin/AdminNotificationBanners.vue'),
      meta: {
        area: ApplicationArea.Admin,
        friendlyName: 'Notification Banners',
      },
    },
    {
      path: '/admin/settings/interfaces',
      name: 'admininterfaces',
      component: () => import(/* webpackChunkName: "admininterfaces" */ './views/admin/AdminInterfaces.vue'),
      meta: {
        area: ApplicationArea.Admin,
        friendlyName: 'Interfaces',
      },
    },
    {
      path: '/admin/settings/utils',
      name: 'adminutils',
      component: () => import(/* webpackChunkName: "adminutils" */ './views/admin/AdminUtils.vue'),
      meta: {
        area: ApplicationArea.Admin,
        friendlyName: 'Developer Utilities',
      },
    },
    {
      path: '/admin/settings/deviceTypes',
      name: 'adminDeviceTypes',
      component: () => import(/* webpackChunkName: "deviceTypes" */ './views/admin/AdminDeviceTypes.vue'),
      meta: {
        friendlyName: 'Device Types',
        area: ApplicationArea.Admin,
      },
    },
    {
      path: '/admin/settings/gatewayTypes',
      name: 'adminGatewayTypes',
      component: () => import(/* webpackChunkName: "gatewayTypes" */ './views/admin/AdminGatewayTypes.vue'),
      meta: {
        friendlyName: 'Gateway Types',
        area: ApplicationArea.Admin,
      },
    },
    {
      path: '/admin/settings/deviceType/:id',
      component: () => import(/* webpackChunkName: "adminDeviceTypeSettings" */ './views/admin/AdminDeviceTypeSettings.vue'),
      meta: {
        friendlyName: 'Device Type Settings',
        area: ApplicationArea.Admin,
      },
      children: [
        {
          path: '',
          name: 'deviceTypeDetails',
          component: () => import(/* webpackChunkName: "deviceTypeDetails" */ './views/admin/AdminDeviceTypeDetails.vue'),
          meta: {
            friendlyName: 'Device Type Details',
            area: ApplicationArea.Admin,
          },
        },
        {
          path: 'cataloguedetails',
          name: 'catalogueDetails',
          component: () => import(/* webpackChunkName: "catalogueDetails" */ './views/admin/AdminCatalogueDetails.vue'),
          meta: {
            friendlyName: 'Catalogue Details',
            area: ApplicationArea.Admin,
          },
        },
        {
          path: 'mappingfunctions',
          name: 'mappingFunctionEditor',
          component: () => import(/* webpackChunkName: "mappingFunctionEditor" */ './views/admin/editors/MappingFunctions.vue'),
          meta: {
            friendlyName: 'Mapping Function Editor',
            area: ApplicationArea.Admin,
          },
        },
        {
          path: 'downlinkfunctions',
          name: 'downlinkFunctionEditor',
          component: () => import(/* webpackChunkName: "downlinkFunctionEditor" */ './views/admin/editors/DownlinkFunctions.vue'),
          meta: {
            friendlyName: 'Downlink Function Editor',
            area: ApplicationArea.Admin,
          },
        },
        {
          path: 'barcodefunctions',
          name: 'barcodeFunctionEditor',
          component: () => import(/* webpackChunkName: "barcodeFunctionEditor" */ './views/admin/editors/BarcodeFunctions.vue'),
          meta: {
            friendlyName: 'Barcode Function Editor',
            area: ApplicationArea.Admin,
          },
        },
        {
          path: 'dataschema',
          name: 'dataSchemaEditor',
          component: () => import(/* webpackChunkName: "dataSchemaEditor" */ './views/admin/editors/DataSchema.vue'),
          meta: {
            friendlyName: 'Data Schema Editor',
            area: ApplicationArea.Admin,
          },
        },
        {
          path: 'configurationprofiles',
          name: 'configurationEditor',
          component: () => import(/* webpackChunkName: "configurationEditor" */ './views/admin/editors/ConfigurationProfiles.vue'),
          meta: {
            friendlyName: 'Data Schema Editor',
            area: ApplicationArea.Admin,
          },
        },
        {
          path: 'rules',
          name: 'ruleEditor',
          component: () => import(/* webpackChunkName: "ruleEditor" */ './views/admin/editors/Rules.vue'),
          meta: {
            friendlyName: 'Rule Editor',
            area: ApplicationArea.Admin,
          },
        },
        {
          path: 'interfaces',
          name: 'interfaceEditor',
          component: () => import(/* webpackChunkName: "interfaceEditor" */ './views/admin/editors/InterfaceFunctions.vue'),
          meta: {
            friendlyName: 'Interface Editor',
            area: ApplicationArea.Admin,
          },
        },
        {
          path: 'modbus',
          name: 'modbusEditor',
          component: () => import(/* webpackChunkName: "interfaceEditor" */ './views/admin/editors/Modbus.vue'),
          meta: {
            friendlyName: 'Modbus Editor',
            area: ApplicationArea.Admin,
          },
        },
      ],
    },
    {
      path: '/admin/settings/gatewayType/:id',
      component: () => import(/* webpackChunkName: "adminGatewayTypeSettings" */ './views/admin/AdminGatewayTypeSettings.vue'),
      meta: {
        friendlyName: 'Gateway Type Settings',
        area: ApplicationArea.Admin,
      },
      children: [
        {
          path: '',
          name: 'gatewayTypeDetails',
          component: () => import(/* webpackChunkName: "gatewayTypeDetails" */ './views/admin/AdminGatewayTypeDetails.vue'),
          meta: {
            friendlyName: 'Gateway Type Details',
            area: ApplicationArea.Admin,
          },
        },
      ],
    },
    {
      path: '/admin/settings/system/summary',
      name: 'adminSystemSummary',
      component: () => import(/* webpackChunkName: "adminSystemSummary" */ './views/admin/AdminSystemSummary.vue'),
      meta: {
        friendlyName: 'System Summary',
        area: ApplicationArea.Admin,
      },
    },
    {
      path: '/admin/settings/accounts',
      name: 'adminAccounts',
      component: () => import(/* webpackChunkName: "adminAccounts" */ './views/admin/AdminAccounts.vue'),
      meta: {
        friendlyName: 'Accounts',
        area: ApplicationArea.Admin,
      },
    },
    {
      path: '/apidocs.html',
      redirect: '/apidocs/core_v2.0',
      meta: {
        area: ApplicationArea.Public,
      },
    },
    {
      path: '/apidocs',
      redirect: '/apidocs/core_v2.0',
      component: () => import(/* webpackChunkName: "apiDocs" */ './views/ApiDocs.vue'),
      meta: {
        area: ApplicationArea.Public,
      },
    },
    {
      path: '/apidocs/:version',
      name: 'ApiDocs',
      component: () => import(/* webpackChunkName: "apiDocs" */ './views/ApiDocs.vue'),
      meta: {
        friendlyName: 'Daizy API Docs',
        area: ApplicationArea.Public,
      },
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import(/* webpackChunkName: "dashboard" */ './views/HomeDashboard.vue'),
      meta: {
        area: ApplicationArea.Home,
        friendlyName: 'Dashboard',
      },
      children: [
        {
          path: 'map',
          name: 'expandedmap',
          component: () => import(/* webpackChunkName: "details" */ './components/ExpandedMap.vue'),
          meta: {
            area: ApplicationArea.Home,
            friendlyName: 'expanded map',
          },
        },
        {
          path: '/organisation/:organisationId/dashboard/map',
          name: 'org-proj-expanded-map',
          component: () => import(/* webpackChunkName: "details" */ './components/ExpandedMap.vue'),
          meta: {
            area: ApplicationArea.Home,
            friendlyName: 'expanded map',
          },
        },
      ],
    },
    {
      path: '/connectivity',
      name: 'connectivity-base',
      redirect: RouterHelper.redirectHandler,
      meta: {
        area: ApplicationArea.Compose,
        friendlyName: 'Connectivity',
      },
    },
    {
      path: '/organisation/:organisationId/connectivity',
      component: () => import(/* webpackChunkName: "connectivity" */ './views/Connectivity.vue'),
      meta: {
        area: ApplicationArea.Compose,
        friendlyName: 'Connectivity',
      },
      children: [
        {
          path: 'sigfox-credentials',
          name: 'sigfoxCredentials',
          component: () => import(/* webpackChunkName: "sigfoxCredentials" */ './views/connectivity/SigfoxCredentials.vue'),
          meta: {
            friendlyName: 'Sigfox API Credentials',
            area: ApplicationArea.Compose,
          },
        },
        {
          path: 'lora-settings',
          name: 'loraSettings',
          component: () => import(/* webpackChunkName: "loraSettings" */ './views/connectivity/LoraSettings.vue'),
          meta: {
            friendlyName: 'Loriot Settings',
            area: ApplicationArea.Compose,
          },
        },
        {
          path: 'iot-endpoint',
          name: 'iotEndpoint',
          component: () => import(/* webpackChunkName: "iotEndpoint" */ './views/connectivity/IoTEndpoint.vue'),
          meta: {
            friendlyName: 'IoT Endpoint',
            area: ApplicationArea.Compose,
          },
        },
        {
          path: 'sims',
          name: 'sims',
          component: () => import(/* webpackChunkName: "sim-overview" */ './views/connectivity/SimOverview.vue'),
          meta: {
            area: ApplicationArea.Compose,
            friendlyName: 'Sim Card Overview',
          },
        },
      ],
    },
    {
      path: '/admin/subscriptions',
      name: 'subscriptions',
      component: () => import(/* webpackChunkName: "subscriptions" */ './views/subscriptions/Subscriptions.vue'),
      meta: {
        area: ApplicationArea.Subscriptions,
        friendlyName: 'Subscriptions',
      },
    },
    {
      path: '/admin/subscriptions/plans',
      name: 'subscription-plans',
      component: () => import(/* webpackChunkName: "subscription-plans" */ './views/subscriptions/SubscriptionPlans.vue'),
      meta: {
        area: ApplicationArea.Subscriptions,
        friendlyName: 'Subscription Plans',
      },
    },
    {
      path: '/admin/subscriptions/orders',
      name: 'subscription-orders',
      component: () => import(/* webpackChunkName: "subscription-orders" */ './views/subscriptions/SubscriptionOrders_New.vue'),
      meta: {
        area: ApplicationArea.Subscriptions,
        friendlyName: 'Subscription Orders',
      },
    },
    {
      path: '/admin/subscriptions/orders/old',
      name: 'subscription-orders-old',
      component: () => import(/* webpackChunkName: "subscription-orders" */ './views/subscriptions/SubscriptionOrders.vue'),
      meta: {
        area: ApplicationArea.Subscriptions,
        friendlyName: 'Subscription Orders',
      },
    },
    {
      path: '/admin/subscriptions/simstockmanagement',
      name: 'sim-stock-management',
      component: () => import(/* webpackChunkName: "sim-management" */ './views/subscriptions/SimStockManagement.vue'),
      meta: {
        area: ApplicationArea.Subscriptions,
        friendlyName: 'Sim Stock Management',
      },
    },
    {
      path: '/admin/subscriptions/simproducts',
      name: 'sim-products',
      component: () => import(/* webpackChunkName: "sim-products" */ './views/subscriptions/SimProducts.vue'),
      meta: {
        area: ApplicationArea.Subscriptions,
        friendlyName: 'Sim Products',
      },
    },
    {
      path: '/admin/subscriptions/subscriptionproducts',
      name: 'subscription-products',
      component: () => import(/* webpackChunkName: "subscription-products" */ './views/subscriptions/SubscriptionProducts.vue'),
      meta: {
        area: ApplicationArea.Subscriptions,
        friendlyName: 'Subscription Products',
      },
    },
    {
      path: '/admin/subscriptions/sigfoxPools',
      name: 'sigfox-pools',
      component: () => import(/* webpackChunkName: "sigfox-pools" */ './views/subscriptions/SigfoxSubscriptionPools.vue'),
      meta: {
        area: ApplicationArea.Subscriptions,
        friendlyName: 'Sigfox Pools',
      },
    },
    {
      path: '/account/:accountId/summary',
      name: 'accounts',
      component: () => import(/* webpackChunkName: "accounts" */ './views/accounts/AccountSummary.vue'),
      meta: {
        area: ApplicationArea.Accounts,
        friendlyName: 'Accounts',
      },
    },
    {
      path: '/account/:accountId/access',
      component: () => import(/* webpackChunkName: "accounts" */ './views/accounts/AccountAccess.vue'),
      meta: {
        area: ApplicationArea.Accounts,
        friendlyName: 'Account Access',
      },
      children: [
        {
          path: '/',
          name: 'account-users',
          component: () => import(/* webpackChunkName: "accounts" */ './views/accounts/AccountUsers.vue'),
          meta: {
            area: ApplicationArea.Accounts,
            friendlyName: 'Account Users',
          },
        },
        {
          path: 'keys',
          name: 'account-keys',
          component: () => import(/* webpackChunkName: "accounts" */ './views/accounts/AccountAPIKeys.vue'),
          meta: {
            area: ApplicationArea.Accounts,
            friendlyName: 'Account API Keys',
          },
        },
      ],
    },
    {
      path: '/account/:accountId/subscriptions',
      name: 'account-subs',
      component: () => import(/* webpackChunkName: "accounts" */ './views/accounts/AccountSubscriptions.vue'),
      meta: {
        area: ApplicationArea.Accounts,
        friendlyName: 'Account Subscription',
      },
    },
    {
      path: '/account/:accountId/subscriptions/orders',
      name: 'account-subscription-orders',
      component: () => import(/* webpackChunkName: "accounts" */ './views/accounts/AccountSubscriptionOrders.vue'),
      meta: {
        area: ApplicationArea.Accounts,
        friendlyName: 'Account Subscription Orders',
      },
    },
    { // KEEP THIS ROUTE NEAR LAST
      path: '/404',
      alias: '*',
      component: () => import(/* webpackChunkName: "notfound" */ './views/NotFound.vue'),
      meta: {
        area: ApplicationArea.Misc,
        friendlyName: 'page not found',
      },
    },
    { // NOTE: KEEP THIS ROUTE LAST - Anthing that doesn't go a futher level deep will hit this if higher
      path: '/organisation/:organisationId',
      redirect: '/organisation/:organisationId/projectcomposer',
    },
  ],
});

router.beforeEach(async (to, from, next) => {
  store.commit('setUseGatewaySubscriptions');
  GlobalTokenManager.cancel(true);

  store.commit('setBypass', false);
  store.commit('setOverlay', false);

  // Are we looking at a protected route?
  if (!to.matched.some((record) => record.meta.area === ApplicationArea.Public)) {

    // Everything behind authentication //

    // Check we actually have an API token otherwise hit the login
    if (!store.getters.login || !store.getters.login.token) {
      store.commit('reset');
      store.commit('setShowInterface', true);
      next('/login');
      return;
    }

    // Make sure our API token is set
    await RouterHelper.setLogin();

    if (to.path !== from.path) {
      if (!(await RouterHelper.setOrganisation(to, from, next))) {
        return;
      }
      if (!(await RouterHelper.setAccount(to, from, next))) {
        return;
      }
      if (to.path.includes('/organisation/')) {
        const org = store.getters.organisations.organisations.find((organisation: Organisation) => organisation.id === Number(to.params.organisationId));
        if (org && org.billing_account) {
          store.commit('selectAccount', org.billing_account.id);
        } else {
          store.commit('clearAccount');
        }
      }
    }
    if (to.meta && to.meta.area) {
      const requiredPermissions: { [k in ApplicationArea]?: string} = {
        [ApplicationArea.Home]: 'compose:view',
        [ApplicationArea.Admin]: 'administration:view',
        [ApplicationArea.Subscriptions]: 'subscription:view',
        [ApplicationArea.Publish]: 'publish:view',
        [ApplicationArea.Compose]: 'compose:view',
        [ApplicationArea.Consume]: 'consume:view',
        [ApplicationArea.Inventory]: 'inventory:view',
      };
      await store.dispatch('retrieveActiveNotificationBanner');

      const requiredPermission = requiredPermissions[to.meta.area as ApplicationArea];

      if (requiredPermission && !store.getters.hasPermission(requiredPermission)) {
        next('/');
        return;
      }
    }
  }

  // Show interface and get on and render
  store.commit('setShowInterface', true);
  next();
});

// handles case after deployment where a lazy loaded chunk has been updated
router.onError((error) => {
  if (/loading chunk \d* failed./i.test(error.message)) {
    window.location.reload();
  }
});

if (process.env.VUE_APP_ANALYTICS_TRACKING_ID) {
  Vue.use(VueAnalytics, {
    id: process.env.VUE_APP_ANALYTICS_TRACKING_ID,
    router,
  });
}

router.onReady(async () => {
  try {
    // tslint:disable-next-line
    console.log('Router ready!');
    if (router.currentRoute.matched.some((record) => record.meta.area === ApplicationArea.Compose) && store.getters.login && store.getters.login.token) {
      await store.dispatch('addProjectsAsync',
        {
          take: 0,
          skip: 0,
          sort: '',
          desc: '',
          filters: {},
        },
      );
    }
  } catch (e) {
    const err = e as Error;
    // tslint:disable-next-line
    console.error(err);
    // store.dispatch('addToastErrorMessage', err.message);
  }
});

export default router;
